import { DisposableGroup, type Disposable } from '@blocksuite/global/utils';
import type { DocCollection } from '@blocksuite/store';
import { DebugLogger } from '@pique/debug';
import type { LoadedBlockSuiteDoc } from '@toeverything/infra';
import { useEffect, useState } from 'react';

const logger = new DebugLogger('use-doc-collection-page');

export function useDocCollectionPage(
  docCollection: DocCollection,
  pageId: string | null
): LoadedBlockSuiteDoc | null {
  const [page, setPage] = useState<LoadedBlockSuiteDoc | null>(null);

  useEffect(() => {
    const group = new DisposableGroup();
    let readyDisposable: Disposable | null = null;
    const disposeReadyListener = () => {
      readyDisposable?.dispose();
      readyDisposable = null;
    };

    const resolvePage = (nextPageId: string | null) => {
      if (!nextPageId) {
        disposeReadyListener();
        setPage(null);
        return;
      }

      const nextPage = docCollection.getDoc(nextPageId);
      if (!nextPage) {
        disposeReadyListener();
        setPage(null);
        return;
      }

      try {
        if (nextPage.ready) {
          disposeReadyListener();
          setPage(nextPage as LoadedBlockSuiteDoc);
        } else {
          setPage(null);
          disposeReadyListener();
          const setLoadedPage = () => {
            if (nextPage.ready) {
              disposeReadyListener();
              setPage(nextPage as LoadedBlockSuiteDoc);
            }
          };
          readyDisposable = nextPage.slots.ready.on(setLoadedPage);
          nextPage.load(setLoadedPage);
          setLoadedPage();
        }
      } catch (err) {
        logger.error('Failed to load page', err);
        disposeReadyListener();
        setPage(null);
      }
    };

    resolvePage(pageId);
    group.add(
      docCollection.slots.docAdded.on(id => {
        if (pageId === id) {
          resolvePage(id);
        }
      })
    );
    group.add(
      docCollection.slots.docRemoved.on(id => {
        if (pageId === id) {
          disposeReadyListener();
          setPage(null);
        }
      })
    );
    return () => {
      disposeReadyListener();
      group.dispose();
    };
  }, [docCollection, pageId]);

  return page;
}
